import { RxStatus } from '@truepill/tpos-types'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import type { Fill, Prescription } from 'types'
import { PrescriptionOrigin } from 'types'

export const getCurrentFill = (prescription: Prescription, fillId?: string): Fill | undefined => {
  const { fills } = prescription
  if (!fills.length) {
    return
  }

  if (fillId) {
    for (const fill of fills) {
      if (fill._id === fillId) {
        return fill
      }
    }
    return
  }

  return fills.slice(-1)[0]
}

export const matchRXLabel = (rxLabel: string): { rxNumber?: string; fillIndex?: string } => {
  const rxLabelMatches = rxLabel.match(/(\d+)-(\d+)-(\d\d[CP]?)/)
  if (!rxLabelMatches) {
    return {}
  }
  let fillIndex = rxLabelMatches[3]
  // Some barcode readers will automatically trim trailing 0s out of the box
  // and getting all hardware set up correctly is more of pain than just
  // fixing it in software
  if (!fillIndex.match(/\d{2}/)) {
    fillIndex = '0' + fillIndex
  }

  return {
    rxNumber: rxLabelMatches[2],
    fillIndex,
  }
}

export const hasOpenFills = (prescription: Prescription): boolean => prescription.status === RxStatus.InFulfillment

export function calculateFillsRemaining(prescription: Prescription): number {
  return Math.ceil(prescription.quantityRemaining / (prescription.quantity as number))
}

export function calculateExpirationDate(prescription: Prescription, defaultDate?: string): Dayjs {
  if (prescription.expirationDate == null) {
    return dayjs(prescription.writtenDate).add(1, 'year')
  }
  return dayjs(prescription.expirationDate || defaultDate)
}

export const getLastFillDate = (prescription: Prescription): string | undefined => {
  if (prescription.lastEquivalentDrugDispensedInfo) {
    const {
      dispensed: { dispensedAt },
    } = prescription.lastEquivalentDrugDispensedInfo
    return dayjs(dispensedAt).format('YYYY-MM-DD')
  }
}

// Check if prescription has been verified through any of the valid verification methods
export const isPrescriptionVerified = (prescription: Prescription): boolean => {
  const hasVerification =
    prescription.verifications?.pv1 ||
    prescription.verifications?.transferOut ||
    prescription.fills?.some(fill => fill.verifications?.pv1)

  return !!hasVerification
}

export const isForwardedPrescription = (prescription: Prescription): boolean => {
  // Check if prescription is electronic
  const isElectronic = prescription.origin === PrescriptionOrigin.ELECTRONIC

  // Check if prescription has not been verified
  const isNotVerified = !isPrescriptionVerified(prescription)

  // Check if prescription has not been filled
  const isNotYetFilled = prescription.nextFillNumber === 0

  // Check if prescription has transfers (indicating it was transferred)
  const hasTransfers = prescription.transfers && prescription.transfers.length > 0

  return isElectronic && isNotVerified && isNotYetFilled && hasTransfers
}

/**
 * NY removed the single-fill transfer limit for non-controlled meds (10/2025 law).
 * Controlled substances keep the limit, so the NY transfer warnings only apply to them.
 */
// Anything except explicit false is treated as controlled, matching the backend's conservative default.
export const isControlledPrescription = (prescription: Prescription): boolean =>
  prescription.isControlledSubstance !== false
